import {IEvent} from "@webng-types/write-model";
import {exactSizeImageUrl} from "../converter/imageUrl";
import {expandImageUrl} from "../ApiImage";
import {LocalStatus} from "../Constants";

export const BACKGROUND_TEXT_LIGHT_CLASS = "tik4-event--bg-text-light"
export const BACKGROUND_TEXT_DARK_CLASS = "tik4-event--bg-text-dark"

interface BackgroundImageTier {
  suffix: string
  width: number
  height: number
}

// One CDN-resized variant per event-row breakpoint tier (tik4-event--xs/sm/md/lg/xl,
// thresholds defined alongside useBreakpoints in LiveblogEventRow.tsx), each cropped
// ("cover"/c_fill) to approximate that tier's real display aspect ratio instead of a
// single oversized square. The CSS cascade in tik4-event.css picks the matching one.
const BACKGROUND_IMAGE_TIERS: BackgroundImageTier[] = [
  {suffix: "base", width: 460, height: 800},
  {suffix: "xs", width: 576, height: 800},
  {suffix: "sm", width: 768, height: 800},
  {suffix: "md", width: 992, height: 800},
  {suffix: "lg", width: 1200, height: 800},
  {suffix: "xl", width: 1250, height: 800},
]

// WCAG contrast crossover: backgrounds lighter than this get dark text
export const LUMINANCE_THRESHOLD = 0.179

interface Rgb {
  r: number
  g: number
  b: number
}

export function parseHexColor(value: string|undefined): Rgb|undefined {
  if(!value) return undefined
  const match = /^#(?:([0-9a-f]{6})|([0-9a-f]{3}))$/i.exec(value.trim())
  if(!match) return undefined
  const hex = match[1] || match[2].split("").map(c => c + c).join("")
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16)
  }
}

export function relativeLuminance(color: Rgb): number {
  const [r, g, b] = [color.r, color.g, color.b].map(v => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export interface ResolvedEventBackground {
  backgroundValue: string
  // true when cssVarOverrides carries one --t4-ev-cbg-<tier> variable per breakpoint
  // tier for the CSS cascade to pick from; LiveblogEventRow must then leave --t4-ev-cbg
  // itself unset inline, or its higher inline specificity would always win and defeat
  // the per-tier selection.
  isTiered: boolean
  textStyleClassName: string
  cssVarOverrides?: Record<string, string>
}

function textStyleClassName(scrimColor: Rgb): string {
  return relativeLuminance(scrimColor) > LUMINANCE_THRESHOLD ? BACKGROUND_TEXT_DARK_CLASS : BACKGROUND_TEXT_LIGHT_CLASS
}

function cssUrl(url: string): string {
  // \n \r \f terminate CSS string tokens and would let the rest of the URL
  // escape the declaration (CSS injection via the SSR sharing page)
  return `url("${url
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\a ")
    .replace(/\r/g, "\\d ")
    .replace(/\f/g, "\\c ")}")`
}

function clampedAlpha(opacity: number): number {
  return Math.round(Math.min(100, Math.max(0, opacity)) / 100 * 1000) / 1000
}

export function resolveEventBackground(event: IEvent, apiHost: string): ResolvedEventBackground|undefined {
  const background = event.background
  if(!background) return undefined

  const explicitColor = parseHexColor(background.color) ? background.color!.trim() : undefined
  const explicitOpacity = background.opacity

  const mediaLocalId = background.image?.media_local_id
  const media = mediaLocalId ? event.media?.find(m => m.local_id === mediaLocalId && m.local_status !== LocalStatus.Deleted) : undefined

  let tierImageValues: Record<string, string>|undefined
  if(media?.url) {
    tierImageValues = {}
    for(const tier of BACKGROUND_IMAGE_TIERS) {
      const scaled = exactSizeImageUrl(media.url, {width: tier.width, height: tier.height, fit: "cover", format: "webp"})
      tierImageValues[`--t4-ev-cbg-${tier.suffix}`] = cssUrl(expandImageUrl(apiHost, scaled.url)!)
    }
  }

  if(tierImageValues) {
    const cssVarOverrides: Record<string, string> = {...tierImageValues}

    if(explicitColor) {
      cssVarOverrides["--t4-ev-cbgcolor"] = explicitColor.trim()
      if(explicitOpacity !== undefined) {
        cssVarOverrides["--t4-ev-scrim-alpha"] = String(clampedAlpha(explicitOpacity))
      }
    }

    return {
      // Smallest tier's variant, for simple non-tiered consumers (e.g. the editor's
      // small background swatch preview) that just want one representative image value.
      backgroundValue: tierImageValues[`--t4-ev-cbg-${BACKGROUND_IMAGE_TIERS[0].suffix}`],
      isTiered: true,
      cssVarOverrides,
      textStyleClassName: explicitColor
        ? textStyleClassName(parseHexColor(explicitColor)!)
        : BACKGROUND_TEXT_LIGHT_CLASS,
    }
  } else if(explicitColor) {
    // A raw color is not a valid background-image layer, so render the flat
    // fill through the scrim gradient and keep the image layer at "none"
    return {
      backgroundValue: "none",
      isTiered: false,
      cssVarOverrides: {"--t4-ev-cbgcolor": explicitColor.trim()},
      textStyleClassName: textStyleClassName(parseHexColor(explicitColor)!)
    }
  } else {
    return undefined
  }
}
